/**
 * Comment-preserving editor for `skaile.manifest.yaml` files.
 *
 * The publication counterpart to {@link WorkspaceYamlEditor}: it owns the
 * `publisher` / `version` / `assets[]` accessors that used to sit on the
 * workspace editor before the 2026-06-03 manifest/workspace file split. Uses the
 * `yaml` library's Document AST so untouched sections stay byte-identical.
 *
 * Usage:
 *
 *   import { ManifestYamlEditor } from '@skaile/workspaces/core'
 *
 *   const editor = ManifestYamlEditor.load('/project/skaile.manifest.yaml')
 *   editor.setPublisher('skaile-ai')
 *   editor.setAsset({ kind: 'skill', name: 'audit', root: 'skills/audit' })
 *   editor.save()
 */

import { existsSync, readFileSync, writeFileSync } from "node:fs";
import { Document, isMap, isSeq, parseDocument, type YAMLMap } from "yaml";
import type { AssetEntry } from "./publish-manifest.js";

/**
 * Comment-preserving editor for `skaile.manifest.yaml` files.
 * Create with `ManifestYamlEditor.load(filePath)` or `ManifestYamlEditor.create(filePath)`.
 * @docLink packages/core/publish-manifest#manifest-yaml-editor
 */
export class ManifestYamlEditor {
  readonly path: string;
  private doc: Document;

  private constructor(path: string, doc: Document) {
    this.path = path;
    this.doc = doc;
  }

  /** Load an existing skaile.manifest.yaml file. */
  static load(filePath: string): ManifestYamlEditor {
    if (!existsSync(filePath)) {
      throw new Error(`File not found: ${filePath}`);
    }
    const raw = readFileSync(filePath, "utf-8");
    return new ManifestYamlEditor(filePath, parseDocument(raw));
  }

  /** Create a new empty document (for use when no file exists yet). */
  static create(filePath: string): ManifestYamlEditor {
    return new ManifestYamlEditor(filePath, new Document({}));
  }

  /** Write the document back to disk, preserving comments and formatting. */
  save(): void {
    writeFileSync(this.path, this.doc.toString());
  }

  /** Return the serialized YAML string without writing to disk. */
  toString(): string {
    return this.doc.toString();
  }

  // ── Publisher / version / repo-level defaults ───────────────────────────────

  /** Get the `publisher:` field. */
  getPublisher(): string | undefined {
    const v = this.doc.get("publisher");
    return v != null ? String(v) : undefined;
  }

  /** Set the `publisher:` field. */
  setPublisher(p: string): void {
    this.doc.set("publisher", p);
  }

  /** Get the `version:` field. */
  getVersion(): string | undefined {
    const v = this.doc.get("version");
    return v != null ? String(v) : undefined;
  }

  /** Set the `version:` field. */
  setVersion(v: string): void {
    this.doc.set("version", v);
  }

  /** Get the repo-level `license:` default. */
  getLicense(): string | undefined {
    const v = this.doc.get("license");
    return v != null ? String(v) : undefined;
  }

  /** Set the repo-level `license:` default. */
  setLicense(v: string): void {
    this.doc.set("license", v);
  }

  /** Get the repo-level `homepage:` default. */
  getHomepage(): string | undefined {
    const v = this.doc.get("homepage");
    return v != null ? String(v) : undefined;
  }

  /** Set the repo-level `homepage:` default. */
  setHomepage(v: string): void {
    this.doc.set("homepage", v);
  }

  // ── Assets ──────────────────────────────────────────────────────────────────

  /** Get all `assets:` entries. */
  getAssets(): AssetEntry[] {
    const node = this.doc.get("assets", true);
    if (!isSeq(node)) return [];
    return node.toJSON() as AssetEntry[];
  }

  /** Add or update an asset entry, matched by `kind` + `name`. */
  setAsset(entry: AssetEntry): void {
    const node = this.doc.get("assets", true);
    if (!node || !isSeq(node)) {
      this.doc.set("assets", this.doc.createNode([entry]));
      return;
    }
    let found = false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (
        isMap(item) &&
        (item as YAMLMap).get("kind") === entry.kind &&
        (item as YAMLMap).get("name") === entry.name
      ) {
        node.set(i, this.doc.createNode(entry));
        found = true;
        break;
      }
    }
    if (!found) node.add(this.doc.createNode(entry));
  }

  /** Remove an asset entry by kind + name. Returns true when removed. */
  removeAsset(kind: string, name: string): boolean {
    const node = this.doc.get("assets", true);
    if (!node || !isSeq(node)) return false;
    for (let i = 0; i < node.items.length; i++) {
      const item = node.items[i];
      if (
        isMap(item) &&
        (item as YAMLMap).get("kind") === kind &&
        (item as YAMLMap).get("name") === name
      ) {
        node.delete(i);
        return true;
      }
    }
    return false;
  }

  // ── Raw access (escape hatch) ─────────────────────────────────────────────

  /** Get the underlying yaml Document for advanced manipulation. */
  get document(): Document {
    return this.doc;
  }
}
